import type {
  AppliedInstruction,
  ChangeEntry,
  InstructionContext,
  InstructionHistory,
  NoteChange,
} from '@/components/session/note-instructions/noteInstructions.types';
import { tasksQueryKey } from '@/hooks/useTasks';
import type { Document } from '@/lib/mockData';
import {
  EMPTY_HISTORY,
  StaleContextError,
  appendEntry,
  applyResponse,
  assertFresh,
  canUndo,
  changeWriters,
  markUndone,
  revertEntry,
  taskDocId,
  toTaskPayload,
} from '@/lib/noteInstructionChanges';
import { postNoteInstruction } from '@/lib/noteInstructions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';

export const INSTRUCT_ERROR = 'Ekki tókst að vinna fyrirmælin — reyndu aftur eða orðaðu þau öðruvísi.';
export const SAVE_ERROR = 'Ekki tókst að vista allar breytingarnar — farðu yfir nótuna og verkefnin.';
export const STALE_ERROR = 'Nótan eða verkefnin breyttust á meðan fyrirmælin voru unnin — reyndu aftur.';
export const UNDO_ERROR = 'Nótunni eða verkefnum hefur verið breytt síðan — ekki er hægt að afturkalla.';
const NO_CHANGE_REPLY = 'Engu var breytt.';

interface UseNoteInstructionsOptions {
  sessionId?: string;
  patientId?: number;
  /** Saves the note text to `sessionId` through the page's summary save path; rejects when
   * that is no longer the page's session. */
  persistNote: (text: string, sessionId: string) => Promise<void>;
  /** The page's current note (edited or saved) and tasks; re-read after every await. */
  currentNote: string;
  currentTasks: Document[];
}

export const useNoteInstructions = ({
  sessionId,
  patientId,
  persistNote,
  currentNote,
  currentTasks,
}: UseNoteInstructionsOptions) => {
  const queryClient = useQueryClient();
  // Latest page state, so checks after an await never compare against a stale closure.
  const latest = useRef({ sessionId, note: currentNote, tasks: currentTasks });
  useLayoutEffect(() => {
    latest.current = { sessionId, note: currentNote, tasks: currentTasks };
  });
  const [history, setHistory] = useState<InstructionHistory>(EMPTY_HISTORY);
  const [noteChange, setNoteChange] = useState<NoteChange | null>(null);
  const [highlightedTaskIds, setHighlightedTaskIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // The model's summary when an instruction changed nothing (otherwise the ledger shows it).
  const [reply, setReply] = useState<string | null>(null);
  // The page stays mounted across sessions: start each session with a clean history.
  const [historySessionId, setHistorySessionId] = useState(sessionId);
  if (historySessionId !== sessionId) {
    setHistorySessionId(sessionId);
    setHistory(EMPTY_HISTORY);
    setNoteChange(null);
    setHighlightedTaskIds(new Set());
    setError(null);
    setReply(null);
  }

  // The one place the visible "what just changed" state is set: the note diff and the touched
  // tasks stay marked together until the next change, undo or reset.
  const showChange = (note: { prev: string; next: string } | null, taskDbIds: number[]) => {
    setNoteChange(note ? { id: crypto.randomUUID(), ...note } : null);
    setHighlightedTaskIds(new Set(taskDbIds.map(taskDocId)));
  };

  const writersFor = (sid: string) =>
    changeWriters({ queryClient, sessionId: sid, patientId, persistNote, latest: () => latest.current });
  // The page switched session while a write was pending: its result belongs to the old one.
  const movedFrom = (sid: string) => latest.current.sessionId !== sid;

  const invalidateTasks = () => queryClient.invalidateQueries({ queryKey: tasksQueryKey(sessionId) });

  const resetHistory = (message: string) => {
    setHistory(EMPTY_HISTORY);
    showChange(null, []);
    setError(message);
  };

  const instructMutation = useMutation({
    mutationFn: async ({ context, requestSessionId }: { context: InstructionContext; requestSessionId: string }) => {
      const response = await postNoteInstruction({
        session_id: requestSessionId,
        instruction: context.instruction,
        note: context.note,
        tasks: toTaskPayload(context.tasks),
        transcript: context.transcript || null,
      });
      // The page moved to another session mid-request: never write this reply into it.
      if (movedFrom(requestSessionId)) throw new StaleContextError();
      assertFresh(context, response, latest.current);
      return applyResponse(context, response, requestSessionId, writersFor(requestSessionId));
    },
  });

  const undoMutation = useMutation({
    mutationFn: (entry: ChangeEntry) => revertEntry(entry, writersFor(entry.sessionId)),
  });

  const recordEntry = ({ entry, failed }: AppliedInstruction) => {
    if (failed) {
      setError(SAVE_ERROR);
      void invalidateTasks();
    }
    if (entry.targets.length === 0) {
      setReply(entry.summary || NO_CHANGE_REPLY);
      return;
    }
    setHistory((prev) => appendEntry(prev, entry));
    showChange(
      entry.note,
      entry.taskChanges.map((change) => change.dbId)
    );
  };

  const submit = async (context: InstructionContext): Promise<boolean> => {
    setError(null);
    setReply(null);
    let result: AppliedInstruction;
    try {
      if (!sessionId) throw new Error('No session');
      result = await instructMutation.mutateAsync({ context, requestSessionId: sessionId });
    } catch (err) {
      if (sessionId && movedFrom(sessionId)) return false;
      setError(err instanceof StaleContextError ? STALE_ERROR : INSTRUCT_ERROR);
      return false;
    }
    if (movedFrom(sessionId)) return false;
    recordEntry(result);
    return !result.failed;
  };

  const findEntry = (entryId: string) => history.entries.find((entry) => entry.id === entryId);

  const undoEntry = async (entryId: string) => {
    const entry = findEntry(entryId);
    if (!entry?.applied || undoMutation.isPending || instructMutation.isPending) return;
    setError(null);
    setReply(null);
    if (entry.sessionId !== sessionId || !canUndo(entry, latest.current.note, latest.current.tasks)) {
      // What it touched has changed since: the entry stays listed, it just can't be undone.
      setError(UNDO_ERROR);
      return;
    }
    try {
      await undoMutation.mutateAsync(entry);
      if (movedFrom(entry.sessionId)) return;
      setHistory((prev) => markUndone(prev, entry.id));
      // Undo restores without marking anything.
      showChange(null, []);
    } catch {
      if (movedFrom(entry.sessionId)) return;
      // A partial undo leaves history out of sync with reality: drop it and refetch tasks.
      resetHistory(SAVE_ERROR);
      void invalidateTasks();
    }
  };

  const isBusy = instructMutation.isPending || undoMutation.isPending;
  // Whether what the entry touched is still as it left it (busy is a separate, passing reason).
  const isUndoable = (entryId: string) => {
    const entry = findEntry(entryId);
    return !!entry?.applied && entry.sessionId === sessionId && canUndo(entry, currentNote, currentTasks);
  };

  return {
    submit,
    undoEntry,
    isUndoable,
    entries: history.entries,
    isPending: instructMutation.isPending,
    isBusy,
    error,
    reply,
    noteChange,
    dismissNoteChange: () => setNoteChange(null),
    highlightedTaskIds,
  };
};

export type NoteInstructions = ReturnType<typeof useNoteInstructions>;
